// Keyed by `${formatterName}:${value}` so a getter that recomputes on every
// render logs each distinct offender once instead of flooding the console.
const warned = new Set<string>();

/** Matches a `YYYY-MM-DD` prefix followed by anything time-like (`T`/space + a digit). */
const HAS_TIME_COMPONENT = /^\d{4}-\d{2}-\d{2}[T ]\d/;

/**
 * Diagnostic for the date-only formatters (`formatISODateToDisplay`,
 * `formatDateString`). Both read only the leading `YYYY-MM-DD` and discard the
 * rest, so passing a full ISO instant silently drops the time and offset. This
 * logs when that happens.
 *
 * Two distinct ways the discard goes wrong, both caught here:
 * - an offset-bearing instant (`...T19:00:00-05:00`) whose leading date is not
 *   its UTC calendar date, so the sliced date is simply the wrong day;
 * - a genuine event instant, where the reader's zone — not UTC — is the right
 *   frame, so UTC pinning shows the wrong day near midnight.
 *
 * Deliberately does not repair the value. An instant has two defensible
 * renderings and only the call site knows which it wants: its UTC calendar date
 * when the value is a calendar date living in a datetime column, or a zoned
 * wall-clock time when it is a real event. Guessing here would trade a visible
 * bug for a silent one.
 *
 * Side effect: writes to `console.warn`, at most once per formatter/value pair
 * for the lifetime of the page. Never throws.
 */
export function warnIfNotDateOnly(value: string, formatterName: string): void {
  if (!HAS_TIME_COMPONENT.test(value)) return;

  const key = `${formatterName}:${value}`;
  if (warned.has(key)) return;
  warned.add(key);

  console.warn(
    `${formatterName}() received a timestamp, not a date: "${value}". ` +
      'It reads only the leading YYYY-MM-DD and discards the time and offset, ' +
      'so the rendered day can be wrong. Format the instant at the call site ' +
      'instead — as its UTC calendar date if the value is a calendar date ' +
      'stored in a datetime column, or as a zoned time if it is a real event.'
  );
}

/** Test seam — clears the once-per-pair log memory. */
export function resetDateOnlyWarnings(): void {
  warned.clear();
}
